import { Inmate } from '@reliance/types/models/Inmate';
import { InmateMergeHistory } from '@reliance/types/models/InmateMergeHistory';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import api from '../../common/api';
import MergeHistory from './MergeHistory';
import SummaryCard from './SummaryCard';
import ContactsTable from './ContactsTable';
import MatchingInmatesTable from './MatchingInmatesTable';
import MergeButton from './MergeButton';
import { InmateContact } from '@reliance/types/models/InmateContact';
import { useAlerts } from '../../alert/AlertContext';

type MergeAllProps = {
  inmate: Inmate;
  onRefresh(): any;
  tabSelected?: boolean;
};

export type MatchingInmate = Partial<Inmate> & {
  contacts: Partial<InmateContact>[];
  canBeReMerged?: boolean;
  messagingBalance: number;
  audioVideoBalance: number;
};

type MergePreviewResponse = {
  success: boolean;
  preview: boolean;
  inmates: MatchingInmate[];
  totals: {
    messagingBalance: number;
    audioVideoBalance: number;
    contactCount: number;
  };
  mergeHistory?: InmateMergeHistory[];
  alreadyMergedInmateIds?: number[];
};

const MergeAll: React.FC<MergeAllProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [previewData, setPreviewData] = useState<MergePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const alerts = useAlerts();

  // Loads the matching inmates data from the API
  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await api.post<MergePreviewResponse>('/inmate/merge/all', {
        inmateId: props.inmate.id,
        preview: true
      });
      setPreviewData(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'An error occurred while loading the preview';
      setError(errorMessage);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load when tab is selected and we haven't loaded yet
    if (props.tabSelected && previewData === null && !loading) {
      loadPreview();
    }
  }, [props.tabSelected, props.inmate.id]);

  // Gets the formatted merge date (YYYY/MM/DD) for an inmate from merge history
  const getMergeDateForInmate = (inmateId: number): string | null => {
    if (!previewData?.mergeHistory) {
      return null;
    }
    const historyEntry = previewData.mergeHistory.find(h => h.fromInmateId === inmateId);
    if (!historyEntry) {
      return null;
    }
    // Format date as YYYY/MM/DD
    const date = new Date(historyEntry.dateMerged);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  // Calculates totals for all matching inmates
  const getResolvedTotals = () => {
    if (!previewData) {
      return {
        messagingBalance: 0,
        audioVideoBalance: 0,
        contactCount: 0,
        inmateCount: 0,
        totalInmateCount: 0,
        facilityCount: 0,
        alreadyMergedCount: 0
      };
    }

    const messagingBalance = previewData.inmates.reduce((sum, inmate) => sum + inmate.messagingBalance, 0);
    const audioVideoBalance = previewData.inmates.reduce((sum, inmate) => sum + inmate.audioVideoBalance, 0);
    const contactCount = previewData.inmates.reduce((sum, inmate) => sum + inmate.contacts.length, 0);

    // Count unique facilities
    const facilities = new Set(previewData.inmates.map(i => i.facility.id));

    // Count already merged inmates
    const alreadyMergedCount = previewData.inmates.filter(i =>
      previewData.alreadyMergedInmateIds?.includes(i.id)
    ).length;

    return {
      messagingBalance,
      audioVideoBalance,
      contactCount,
      inmateCount: previewData.inmates.length,
      totalInmateCount: previewData.inmates.length,
      facilityCount: facilities.size,
      alreadyMergedCount
    };
  };

  // Determines if the merge can be executed (has inmates to merge)
  const canExecute = (): boolean => {
    if (!previewData || previewData.inmates.length === 0) {
      return false;
    }
    return true;
  };

  // Executes the merge operation API call
  const executeMerge = async () => {
    if (!canExecute()) {
      return;
    }

    setExecuting(true);
    setError(null);
    setSuccess(false);

    try {
      await api.post<MergePreviewResponse>('/inmate/merge/all', {
        inmateId: props.inmate.id,
        preview: false
      });

      setSuccess(true);
      alerts.success('The merge was completed successfully');
      props.onRefresh();
      // Reload preview to show updated state
      setTimeout(() => {
        loadPreview();
      }, 1000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'An error occurred while executing the merge';
      setError(errorMessage);
    } finally {
      setExecuting(false);
    }
  };

  if (loading && !previewData) {
    return (
      <div className="text-center mb-3">
        <i className="fas fa-spin fa-spinner"></i> Loading matching inmates...
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
          Merge completed successfully!
        </Alert>
      )}

      <div className="mb-3">
        <Button
          variant="primary"
          onClick={loadPreview}
          disabled={loading || executing}>
          {loading ? (
            <>
              <i className="fas fa-spin fa-spinner"></i> Loading...
            </>
          ) : (
            <>
              <i className="fas fa-sync"></i> Refresh
            </>
          )}
        </Button>
      </div>

      {previewData && (
        <>
          {previewData.mergeHistory && previewData.mergeHistory.length > 0 && (
            <MergeHistory history={previewData.mergeHistory} />
          )}

          {previewData.inmates.length === 0 ? (
            <Alert variant="info">
              No matching inmates found at other facilities.
            </Alert>
          ) : (
            <>
              <MatchingInmatesTable
                inmates={previewData.inmates}
                getMergeDateForInmate={getMergeDateForInmate}
              />

              <ContactsTable resolvedInmates={previewData.inmates} />

              <SummaryCard resolvedTotals={getResolvedTotals()} />

              <MergeButton
                canExecute={canExecute()}
                executing={executing}
                loading={loading}
                hasEligibleInmates={previewData.inmates.length > 0}
                onClick={executeMerge}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MergeAll;
